import type { TowerDesign } from "@/game/towerSpec";
import type { Material } from "@/physics/model";
import { CATALOG } from "@/game/appurtenances";

/**
 * Ashland 149 ft monopole — real PiRod pole on Ponderosa Rd, Ashland MA (Crown
 * Castle BU 822823 / Verizon), from reference_towers/02_engineering_drawings/
 * MA_Ashland_monopole_149ft_structural.pdf (tnxTower SA).
 *   - 18-sided tapered tube, 9 sections, 149.08 ft (45.44 m).
 *   - Base OD 56.125" (1.426 m), tip OD 22.25" (0.565 m); wall 0.25"→0.375".
 *   - TIA-222-H, Risk II, 127 mph, Exp B. Verizon at 123 ft; other carriers 132–148 ft.
 * Grade not tabulated on the pole schedule → the A572-65 typical of PiRod poles.
 * Foundation page not read → competent drilled pier + anchor group (estimate).
 */
const A572_65: Material = { name: "A572 Gr65", E: 200e9, Fy: 448e6, rho: 7850 };
const AZ = [0, (2 * Math.PI) / 3, (4 * Math.PI) / 3];

export const design: TowerDesign = {
  id: "monopole-ashland-149ft",
  name: "Monopole (Ashland 149 ft, PiRod)",
  description: "Real 149 ft PiRod monopole, Ashland MA (Crown Castle BU 822823).",
  author: "reference: MA Ashland 149 ft tnxTower SA",
  spec: {
    type: "monopole",
    height: 45.44,
    stack: {
      faces: 12,
      panels: 14,
      baseRadius: 0.71, // ≈ real 1.426 m base OD
      topRadius: 0.28,
      legSection: "legXXL",
      braceSection: "braceHeavy",
      legMaterial: A572_65,
      braceMaterial: A572_65,
      bracing: "X",
    },
    shell: { baseDiameter: 1.426, topDiameter: 0.565 },
    foundation: {
      soil: "dense-sand",
      base: { type: "pier", width: 2.6, diameter: 2.6, depth: 7.3, concrete: "c30" },
      connection: {
        kind: "anchorBolts", nBolts: 12, bolt: "b1_75", grade: "f1554_105",
        boltCircleDia: 1.3, embedment: 0.45, edgeDistance: 0.3,
        platePlanArea: 1.9, plateThickness: 0.05, plateGrade: "a36rod", fc: 30e6,
      },
    },
    loads: [
      ...AZ.flatMap((azimuth) => [
        { appurtenance: CATALOG.panelSector, height: 37.5, azimuth },
        { appurtenance: CATALOG.panelSector, height: 37.5, azimuth },
        { appurtenance: CATALOG.rru, height: 37.5, azimuth },
      ]),
      ...AZ.map((azimuth) => ({ appurtenance: CATALOG.panelSector, height: 44.5, azimuth })),
      { appurtenance: CATALOG.coaxBundle, height: 44.5, runLength: 44.5 },
    ],
  },
};
